import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ChatView from '../../components/chat/chat-view';
import ErrorComponent from '../../components/error/error';
import FeatureTitle from '../../components/feature-title/feature-title';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';

// Helpers
import {
  composerHint,
  conversationSubtitle,
  conversationTitle,
  guardiansNotSwitchedLinks,
  mergeChanges,
  mergeOlderPage,
  messageToMarkRead,
  participantDescription,
  personName,
  screenNotice,
  sendErrorMessage,
  unconfirmedSends,
} from '../../helpers/conversation-helper';

// Hooks
import usePolling from '../../hooks/use-polling';

// Models
import { BaseContentContainerFormat } from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ConversationService, { newMessageId } from '../../services/conversation-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

import styles from './chat-detail.module.css';

const POLL_MS = 5000;
// A poll answers at most 50 changes; a backlog bigger than this waits for the next tick.
const MAX_POLL_PAGES = 10;

const ConversationScreenContainer = ({ chatId }: { chatId: string }) => {
  const { setToastContent } = useContext(GeneralContext);
  const currentPersonUuid = new StorageService().getEmployee()?.uuid;

  const [service, setService] = useState<ConversationService>(null);
  // The latest answer's header: participants, canLeave and isParticipant change while the screen is open.
  const [header, setHeader] = useState<ConversationScreen>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [error, setError] = useState(false);
  // 404 covers both a conversation that doesn't exist and one this person can't see, so the screen says neither.
  const [notFound, setNotFound] = useState(false);
  // A teacher unlinked from the classroom while the screen is open gets 404 from then on.
  const [lostAccess, setLostAccess] = useState(false);

  const cursor = useRef<string>(null);
  const lastMarked = useRef<string>(null);
  // Polls never overlap: the timer's and the one after a send would both merge from the same cursor.
  const pollQueue = useRef<Promise<void>>(Promise.resolve());
  // Backoff retries a failing poll many times, and a toast for each would pile up.
  const pollFailed = useRef(false);

  const toastError = (text: string) => setToastContent({ isOpen: true, text, type: 'error' });

  useEffect(() => {
    let cancelled = false;

    new SchoolService()
      .getSchoolById(new StorageService().getEmployee().schoolId)
      .then(async (school) => {
        const conversationService = new ConversationService(school.uuid);
        const screen = await conversationService.getConversation(chatId);
        if (cancelled) return;

        cursor.current = screen.cursor;
        setService(conversationService);
        setHeader(screen);
        setMessages(screen.messages);
        setHasOlder(screen.hasMore);
      })
      .catch((loadError) => {
        if (cancelled) return;
        if (loadError?.status === 404) setNotFound(true);
        else setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [chatId]);

  useEffect(() => {
    if (!service || !header) return;

    const upTo = messageToMarkRead(header.isParticipant, messages, lastMarked.current);
    if (!upTo) return;

    lastMarked.current = upTo;
    service.markRead(chatId, upTo).catch(() => {
      if (lastMarked.current === upTo) lastMarked.current = null;
    });
  }, [service, header, messages, chatId]);

  const fetchChanges = async () => {
    for (let page = 0; page < MAX_POLL_PAGES; page++) {
      if (!service || !cursor.current || lostAccess) return;

      const changes = await service.getConversation(chatId, { since: cursor.current });
      cursor.current = changes.cursor;
      setHeader(changes);
      setMessages((current) => mergeChanges(current, changes.messages));
      if (!changes.hasMore) return;
    }
  };

  const pollChanges = () => {
    const run = pollQueue.current.then(fetchChanges);
    pollQueue.current = run.catch(() => undefined);

    return run;
  };

  usePolling(
    () =>
      pollChanges()
        .then(() => {
          pollFailed.current = false;
        })
        .catch((pollError) => {
          if (pollError?.status === 404) {
            setLostAccess(true);
            return;
          }
          if (!pollFailed.current) toastError('Houve um erro ao buscar novas mensagens.');
          pollFailed.current = true;
          throw pollError;
        }),
    POLL_MS,
    { immediate: false }
  );

  const send = (id: string, text: string) => {
    setPending((current) => [...current.filter((message) => message.id !== id), { id, text, status: 'sending' }]);

    service.sendMessage(chatId, id, text).then(
      () => {
        pollChanges().catch(() => undefined);
      },
      (sendError) => {
        setPending((current) =>
          current.map((message) => (message.id === id ? { ...message, status: 'failed' } : message))
        );
        if (sendError?.status === 404) setLostAccess(true);
        toastError(sendErrorMessage(sendError));
      }
    );
  };

  const loadOlder = () => {
    if (messages.length === 0) return;

    setLoadingOlder(true);
    service
      .getConversation(chatId, { before: messages[0].id })
      .then((page) => {
        setMessages((current) => mergeOlderPage(current, page.messages));
        setHasOlder(page.hasMore);
      })
      .catch(() => toastError('Houve um erro ao carregar as mensagens anteriores.'))
      .finally(() => setLoadingOlder(false));
  };

  const leave = () => {
    service
      .leave(chatId)
      .then(() => {
        setToastContent({ isOpen: true, text: 'Você saiu da conversa.', type: 'success' });
        return pollChanges();
      })
      .catch(() => toastError('Houve um erro ao sair da conversa.'));
  };

  if (notFound) {
    return (
      <BaseContentContainer format={BaseContentContainerFormat.singleWithoutActionButtons}>
        <div className={styles.notFound}>
          <FeatureTitle title='conversa não encontrada' />
          <p>Ela não existe ou você não tem acesso a ela.</p>
          <Link to='/mensagens/listar'>voltar para mensagens</Link>
        </div>
      </BaseContentContainer>
    );
  }

  if (error) return <ErrorComponent type='contentFull' />;

  if (!header) return <Loading type='contentFull' />;

  const notSwitched = guardiansNotSwitchedLinks(header);

  return (
    <BaseContentContainer format={BaseContentContainerFormat.singleWithoutActionButtons} withoutShadowBox={true}>
      <ChatView
        header={
          <div className={styles.header}>
            <div className={styles.titles}>
              <FeatureTitle title={conversationTitle(header)} />
              <p className={styles.subtitle}>{conversationSubtitle(header)}</p>
              {notSwitched.length > 0 && (
                <p className={styles.notSwitched}>
                  Ainda na conversa individual com a escola:{' '}
                  {notSwitched.map((guardian, index) => (
                    <React.Fragment key={guardian.uuid}>
                      {index > 0 && ', '}
                      {guardian.linkTo ? <Link to={guardian.linkTo}>{guardian.name}</Link> : guardian.name}
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>
            <span className={styles.spacer} />
            <div className={styles.actions}>
              {header.canLeave && <SubmitButton value='sair da conversa' variant='secondary' onClick={leave} />}
              <SubmitButton
                value={`participantes (${header.participants.length})`}
                variant='secondary'
                onClick={() => setShowParticipants((shown) => !shown)}
              />
              {showParticipants && (
                <section className={styles.participants} aria-label='Participantes'>
                  {header.participants.length === 0 ? (
                    <p className={styles.empty}>Ninguém participa desta conversa.</p>
                  ) : (
                    <ul>
                      {header.participants.map((participant) => (
                        <li key={participant.person.uuid}>
                          <strong>{personName(participant.person)}</strong>
                          <span>{participantDescription(participant)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </div>
          </div>
        }
        messages={messages}
        pending={unconfirmedSends(pending, messages)}
        participants={header.participants}
        currentPersonUuid={currentPersonUuid}
        hasOlder={hasOlder}
        loadingOlder={loadingOlder}
        onLoadOlder={loadOlder}
        canWrite={!header.isReadOnly && !lostAccess}
        notice={lostAccess ? 'Você não tem mais acesso a esta conversa.' : screenNotice(header)}
        composerHint={composerHint(header)}
        onSend={(text) => send(newMessageId(), text)}
        onRetry={(message) => send(message.id, message.text)}
      />
    </BaseContentContainer>
  );
};

// Keyed by the conversation, so opening another one (from a link or a push) starts from a clean screen.
const ChatDetail = () => {
  const { chatId } = useParams<{ chatId: string }>();

  return <ConversationScreenContainer key={chatId} chatId={chatId} />;
};

export default ChatDetail;
